import moment from "moment";

export type DateTimeFormatPreset = "dateTime" | "date" | "time";

const INTL_LOCALE_KEYWORDS = {
    ca: "gregory",
    nu: "latn"
};

const CURRENCY_SUFFIX_LANGUAGES = [
    "tr"
];

const CURRENCY_NUMBER_PATTERN = /[\d.,+\u2212-]/g;

const NARROW_NO_BREAK_SPACE_PATTERN = /\u202F/g;

const POP_DIRECTIONAL_ISOLATE = "\u2069";

const FIRST_STRONG_ISOLATE = "\u2068";

const NO_BREAK_SPACE = "\u00A0";

const MOMENT_LOCALE_LOADERS: Record<string, () => void> = {
    tr: () => {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        require("moment/locale/tr");
    }
};

const DATE_TIME_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

const NUMBER_FORMATTERS = new Map<string, Intl.NumberFormat>();

export const DATE_TIME_FORMAT_OPTIONS: Record<DateTimeFormatPreset, Intl.DateTimeFormatOptions> = {
    dateTime: {
        minute: "2-digit",
        hour: "2-digit",
        year: "numeric",
        month: "short",
        day: "numeric"
    },
    time: {
        minute: "2-digit",
        hour: "2-digit"
    },
    date: {
        year: "numeric",
        month: "short",
        day: "numeric"
    }
};

export const getIntlLocale = ({
    locale
}: {
    locale: string;
}) => {
    const subtags = locale.toLowerCase().split("-");

    const extensionIndex = subtags.indexOf("u");

    const extensionSubtags = extensionIndex === -1 ? [] : subtags.slice(extensionIndex + 1);

    const missingKeywords = Object.entries(INTL_LOCALE_KEYWORDS)
        .filter(([
            key
        ]) => !extensionSubtags.includes(key))
        .map(([
            key,
            type
        ]) => `${key}-${type}`);

    if(!missingKeywords.length) {
        return locale;
    }

    if(extensionIndex === -1) {
        return `${locale}-u-${missingKeywords.join("-")}`;
    }

    return locale.replace(/-u-/i, `-u-${missingKeywords.join("-")}-`);
};

const getNumberFormatter = ({
    options,
    locale
}: {
    options?: Intl.NumberFormatOptions;
    locale: string;
}) => {
    const intlLocale = getIntlLocale({
        locale
    });

    const formatterKey = `${intlLocale}${JSON.stringify(options ?? {})}`;

    const cachedFormatter = NUMBER_FORMATTERS.get(formatterKey);

    if(cachedFormatter) {
        return cachedFormatter;
    }

    const formatter = new Intl.NumberFormat(intlLocale, options);

    NUMBER_FORMATTERS.set(formatterKey, formatter);

    return formatter;
};

const getDateTimeFormatter = ({
    options,
    locale
}: {
    options?: Intl.DateTimeFormatOptions;
    locale: string;
}) => {
    const intlLocale = getIntlLocale({
        locale
    });

    const formatterKey = `${intlLocale}${JSON.stringify(options ?? {})}`;

    const cachedFormatter = DATE_TIME_FORMATTERS.get(formatterKey);

    if(cachedFormatter) {
        return cachedFormatter;
    }

    const formatter = new Intl.DateTimeFormat(intlLocale, options);

    DATE_TIME_FORMATTERS.set(formatterKey, formatter);

    return formatter;
};

export const formatNumber = ({
    options,
    locale,
    value
}: {
    options?: Intl.NumberFormatOptions;
    locale: string;
    value: number;
}) => {
    return getNumberFormatter({
        options,
        locale
    }).format(value);
};

export const formatPercent = ({
    options,
    locale,
    value
}: {
    options?: Omit<Intl.NumberFormatOptions, "style">;
    locale: string;
    value: number;
}) => {
    return getNumberFormatter({
        options: {
            ...options,
            style: "percent"
        },
        locale
    }).format(value);
};

export const formatCurrency = ({
    currency,
    options,
    locale,
    value
}: {
    options?: Omit<Intl.NumberFormatOptions, "currency" | "style">;
    currency: string;
    locale: string;
    value: number;
}) => {
    const formatter = getNumberFormatter({
        options: {
            ...options,
            style: "currency",
            currency
        },
        locale
    });

    const formattedValue = formatter.format(value);

    const language = locale.toLowerCase().split("-")[0] ?? "";

    if(options?.currencyDisplay === "name" || !CURRENCY_SUFFIX_LANGUAGES.includes(language)) {
        return formattedValue;
    }

    const formattedParts = typeof formatter.formatToParts === "function" ? formatter.formatToParts(value) : [];

    const currencyIndex = formattedParts.findIndex((part) => part.type === "currency");

    const currencyPart = formattedParts[currencyIndex];

    if(currencyPart) {
        const amount = formattedParts
            .filter((part, index) => index !== currencyIndex && (part.type !== "literal" || part.value.trim() || Math.abs(index - currencyIndex) !== 1))
            .map((part) => part.value)
            .join("");

        return `${amount}${NO_BREAK_SPACE}${currencyPart.value}`;
    }

    const currencySymbol = formatter.format(0).replace(CURRENCY_NUMBER_PATTERN, "").trim();

    if(!currencySymbol || formattedValue.endsWith(currencySymbol)) {
        return formattedValue;
    }

    return `${formattedValue.replace(currencySymbol, "").trim()}${NO_BREAK_SPACE}${currencySymbol}`;
};

export const formatDateTime = ({
    preset = "date",
    options,
    locale,
    date
}: {
    options?: Intl.DateTimeFormatOptions;
    preset?: DateTimeFormatPreset;
    date: Date | number;
    locale: string;
}) => {
    const time = date instanceof Date ? date.getTime() : date;

    if(Number.isNaN(time)) {
        return "";
    }

    return getDateTimeFormatter({
        options: options ?? DATE_TIME_FORMAT_OPTIONS[preset],
        locale
    }).format(date).replace(NARROW_NO_BREAK_SPACE_PATTERN, " ");
};

export const isolateText = ({
    text
}: {
    text: string;
}) => {
    if(!text) {
        return text;
    }

    return `${FIRST_STRONG_ISOLATE}${text}${POP_DIRECTIONAL_ISOLATE}`;
};

const loadMomentLocale = ({
    locale
}: {
    locale: string;
}) => {
    const loadMomentLocaleData = MOMENT_LOCALE_LOADERS[locale];

    if(!loadMomentLocaleData || moment.locales().includes(locale)) {
        return;
    }

    const globalLocale = moment.locale();

    loadMomentLocaleData();

    moment.locale(globalLocale);
};

export const getMomentLocale = ({
    locale
}: {
    locale: string;
}) => {
    const subtags = locale.toLowerCase().split("-");

    loadMomentLocale({
        locale: subtags[0] ?? ""
    });

    const loadedLocales = moment.locales();

    const candidates = subtags.map((_subtag, index) => subtags.slice(0, subtags.length - index).join("-"));

    return candidates.find((candidate) => loadedLocales.includes(candidate)) ?? moment.locale();
};

export const getMomentLocaleData = ({
    locale
}: {
    locale: string;
}) => {
    return moment.localeData(getMomentLocale({
        locale
    }));
};

export const createMoment = ({
    locale,
    value
}: {
    value?: moment.MomentInput;
    locale: string;
}) => {
    return moment(value).locale(getMomentLocale({
        locale
    }));
};
